import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// ↓ usage of 'exports' field in package.json is available in webpack v5, so use /lib
// eslint-disable-next-line import/no-unresolved
import { MatchReducerRecord, matchFromServer } from '@albacross/frontend-utils/lib/models/records'

import { URL_API } from 'utils/envHelper'

const MATCH_RESULTS_KEY = ['matchResults']

const emptyMatch = () => MatchReducerRecord()

// The match upload posts a raw CSV file (text/csv), so it bypasses utils/http (which JSON-encodes the body).
const uploadMatchFile = async (file: any, ipRequired: boolean) => {
  const query = ipRequired ? '?ip_required=true' : ''
  const res = await fetch(`${URL_API}/companies/match${query}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'text/csv' },
    body: file,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const error: any = new Error('Match upload failed')
    error.errors = data?.errors || []
    throw error
  }
  return res.json()
}

// Matched results + per-row selection are a transient cross-route workflow, so they live in the query cache, not redux.
export const useMatchResults = () =>
  useQuery({
    queryKey: MATCH_RESULTS_KEY,
    queryFn: emptyMatch,
    enabled: false,
    initialData: emptyMatch,
    staleTime: Infinity,
  })

export const useMatchUpload = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, ipRequired = false }: any) => uploadMatchFile(file, ipRequired),
    onMutate: () => queryClient.setQueryData(MATCH_RESULTS_KEY, emptyMatch()),
    onSuccess: (body: any) => queryClient.setQueryData(MATCH_RESULTS_KEY, matchFromServer(body)),
    onError: (error: any) =>
      queryClient.setQueryData(MATCH_RESULTS_KEY, (old: any) =>
        (old || emptyMatch()).set('errors', error?.errors?.length ? error.errors : ['Upload failed']),
      ),
  })
}

export const useToggleMatchSelected = () => {
  const queryClient = useQueryClient()
  return (id: any, selected: boolean) =>
    queryClient.setQueryData(MATCH_RESULTS_KEY, (old: any) =>
      (old || emptyMatch()).setIn(['matched', id, 'selected'], selected),
    )
}

export const useClearMatch = () => {
  const queryClient = useQueryClient()
  return () => queryClient.setQueryData(MATCH_RESULTS_KEY, emptyMatch())
}
